<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  // Place a close button absolutely in the top right corner for a contact modal
  .close-button {
    position: absolute;
    top: utils.in_rem(32px);
    right: utils.in_rem(32px);
  }
</style>

<script>
  import CloseButton from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/CloseButton.svelte";
  import Modal from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/Modal.svelte";
  import { onDestroy, onMount, setContext } from "svelte";
  import { showToast } from "@sourcewhale/core";
  import Body from "./Body.svelte";
  import Heading from "./Heading.svelte";
  import PopUpModal from "./PopUpModal.svelte";
  import {
    activePopUpModalStore,
    activityFeedStore,
    callAnsweredStore,
    candidatePhoneStore,
    candidateStore,
    editContactStore,
    showEditContact,
    todoPanelStore,
    todosModalItemStore,
  } from "./stores";

  import { CONTACT_CTAS_CONTEXT } from "../../elements/ProfileContactSideBarShared/ContactCtas/context";
  import {
    CONTACT_AI_CONTEXT,
    createContactAiContext,
  } from "./Tabs/AskAI/context";

  const stores = createContactAiContext();
  setContext(CONTACT_AI_CONTEXT, stores);

  // Context for the buttons in the profile side bar
  setContext(CONTACT_CTAS_CONTEXT, {
    activePopUpModalStore,
    activityFeedStore,
    callAnsweredStore,
    candidateStore,
    candidatePhoneStore,
    itemStore: todosModalItemStore,
  });

  const todoPanelDirty = todoPanelStore.dirty;
  const editContactDirty = editContactStore.dirty;

  let action = null;
  let actionArgs = [];

  const confirmIfDirty = (f) => {
    if (!f) return null;
    return () => {
      // - Check if any changes have been made in the todo itself or the edit
      //   contact panel
      // - Don't prevent navigation for "bounce" to-dos - the reactivity makes
      //   the dirty state trickier to manage, and preventing navigation doesn't
      //   provide much value (if any)
      if (
        $editContactDirty ||
        ($todoPanelDirty && $todosModalItemStore?.kind !== "bounce")
      ) {
        activePopUpModalStore.set({
          modal: "confirmNavigation",
          cancel: () => {
            activePopUpModalStore.set(null);
            action = null;
          },
          confirm: () => {
            activePopUpModalStore.set(null);
            if (action) action(...actionArgs);
            action = null;
            showToast({
              title: "Success!",
              subtitle: "Discarded unsaved changes",
              type: "success",
            });
          },
        });
        action = f;
        return null;
      }
      return f();
    };
  };

  const onclose = confirmIfDirty(() => {
    const callback = $todosModalItemStore?.onCloseCallback;
    $showEditContact = false;
    $todosModalItemStore = null;
    if (callback) callback();
  });

  $: isTodo = $todosModalItemStore?.toDoId;

  $: left = confirmIfDirty(
    $showEditContact ? null : $todosModalItemStore?.prev
  );
  $: right = confirmIfDirty(
    $showEditContact ? null : $todosModalItemStore?.next
  );

  const handleKeydown = (e) => {
    // Don't allow navigation if a pop up modal is open
    if ($activePopUpModalStore) return;

    // Don't allow navigation if the user is typing in an input field
    const tag = e.target.tagName;
    if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;

    if (e.key === "ArrowLeft" && left) {
      e.preventDefault();
      e.stopPropagation();
      left();
    } else if (e.key === "ArrowRight" && right) {
      e.preventDefault();
      e.stopPropagation();
      right();
    }
  };

  // Add event listener for arrow keys
  onMount(() => {
    document.addEventListener("keydown", handleKeydown);
  });

  // Cleanup event listener on component destroy
  onDestroy(() => {
    document.removeEventListener("keydown", handleKeydown);
  });
</script>

{#if $todosModalItemStore}
  <Modal
    {onclose}
    {left}
    {right}
    showHeader={isTodo}
    closeButtonStyle="padding-right: 0.5rem;"
    modalContentsStyle="width: calc(min(90vw, 75rem)); height: calc(min(90svh, 60rem));"
  >
    <Heading slot="header" />
    <Body />

    <!-- Add a close button to the contact modal, because the header is missing in this case-->
    {#if !isTodo}
      <div class="close-button">
        <CloseButton {onclose} />
      </div>
    {/if}

    <!-- Place all pop up modals here so that any floating UIs won't -->
    <!-- get bound to the elements / components that called them -->
    {#if $activePopUpModalStore}
      <PopUpModal />
    {/if}
  </Modal>
{/if}
